"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.I18nConfiguration = void 0;
const configuration_data_1 = require("./configuration-data");
/**
 * Wrapper class to extract I18n Configuration query QueryOutputs.
 * If the query result is a primitive data value, then the data can be extracted directly,
 * otherwise it can be extracted through the {@link ConfigurationData} object.
 */
class I18nConfiguration {
    constructor(configData) {
        this._configData = configData;
        this._isPrimitiveDataResult = configData.isPrimitiveData();
    }
    /**
     * Extracts the {@link ConfigurationData} returned from the query.
     *
     * If no configuration data was returned from the query then an empty
     * object (object without underlying data) will be returned.
     *
     * @return configuration data
     */
    get configData() {
        return this._configData;
    }
    /**
     * Extracts the resolved configuration in String format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * Any primitive data type that is not already of string format will be converted to a String.
     *
     * @return Resolved Configuration in String format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getStringValue() {
        this.validatePrimitiveResult('getStringValue');
        return this._configData.getStringValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in int format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either int or String format.
     *
     * @return Resolved Configuration in int format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getNumberValue() {
        this.validatePrimitiveResult('getNumberValue');
        return this._configData.getNumberValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in Boolean format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either boolean or String format.
     *
     * @return Resolved Configuration in boolean format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getBooleanValue() {
        this.validatePrimitiveResult('getBooleanValue');
        return this._configData.getBooleanValue(this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Extracts the resolved configuration in Enum format. This method is only call-able
     * if the retrieved configuration is a primitive data type. if this method is called on an object
     * then an exception will be thrown.
     *
     * The configuration can be stored in either number or string format based on the enum type.
     *
     * @param enumType the enum type that the config value belongs to
     * @return Resolved Configuration in enum format.
     *
     * @throws Error    Thrown if called on a non-primitive configuration value
     */
    getEnumValue(enumType) {
        this.validatePrimitiveResult("getEnumValue");
        return this._configData.getEnumValue(enumType, this._configData.getPrimitiveDataConfigName());
    }
    /**
     * Determines whether the {@link I18nConfiguration} object contains any data or not.
     *
     * @return true if empty, false otherwise.
     */
    isEmpty() {
        return this._configData.isEmpty();
    }
    validatePrimitiveResult(methodName) {
        if (!this._isPrimitiveDataResult) {
            throw new Error(`Method ${methodName} cannot be called on a configuration of type object.
                Call getConfigData().${methodName}(configName) instead.`);
        }
    }
    toString() {
        return `I18nConfiguration: ${this._configData}`;
    }
}
exports.I18nConfiguration = I18nConfiguration;
/**
 * Return the {@link Builder} instance used to build an {@link I18nConfiguration} object.
 *
 * @return the builder instance
 */
I18nConfiguration.Builder = class {
    constructor() {
        this._configs = new Map();
    }
    /**
     * Adds a configuration to the {@link I18nConfiguration} container.
     *
     * @param configName name of the config to add.
     * @param value value of the config to add.
     *
     * @return builder instance
     */
    addConfiguration(configName, value) {
        this._configs.set(configName, value);
        return this;
    }
    /**
     * Builds the {@link I18nConfiguration} class using the previously
     * added configurations.
     *
     * @return {@link I18nConfiguration} class instance
     */
    build() {
        return new I18nConfiguration(new configuration_data_1.ConfigurationData(this._configs));
    }
};
//# sourceMappingURL=i18n-configuration.js.map